import { useState, useCallback, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAddresses, type Address } from '@/client/modules/addresses/hooks/useAddresses'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Upload, Loader2, CheckCircle, AlertCircle, Send, Download, BookOpen, PenLine } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'

interface LibraryTemplate {
  id: string
  name: string
  description: string | null
  vertical: string
  merge_fields: Array<{ name: string; label: string; type: string; required: boolean }>
  content_preview: string
}

interface Recipient {
  name: string
  address_line1: string
  address_line2?: string
  suburb: string
  state: string
  postcode: string
  merge_data?: Record<string, string>
}

interface PreviewResult {
  valid_count: number
  error_count: number
  estimated_cost_cents: number
  estimated_cost: string
  errors: Array<{ row: number; errors: string[] }>
}

interface BulkResult {
  created: number
  total_cost_cents: number
  total_cost: string
  order_ids: string[]
  errors: Array<{ row: number; error: string }>
}

const REQUIRED_COLUMNS = ['name', 'address_line1', 'suburb', 'state', 'postcode']

function parseCSV(text: string): Array<Record<string, string>> {
  const lines = text.split('\n').filter(l => l.trim())
  if (lines.length < 2) return []

  const headers = lines[0]!.split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase())
  const rows: Array<Record<string, string>> = []

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i]!.split(',').map(v => v.trim().replace(/^"|"$/g, ''))
    const row: Record<string, string> = {}
    headers.forEach((h, j) => {
      row[h] = values[j] ?? ''
    })
    rows.push(row)
  }

  return rows
}

export function BulkSendPage() {
  const [step, setStep] = useState<'upload' | 'preview' | 'confirm' | 'done'>('upload')
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [colour, setColour] = useState(false)
  const [priority, setPriority] = useState(false)
  const [senderAddressId, setSenderAddressId] = useState<string>('')
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [result, setResult] = useState<BulkResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [contentMode, setContentMode] = useState<'template' | 'custom'>('template')
  const [selectedTemplate, setSelectedTemplate] = useState<LibraryTemplate | null>(null)
  const [customContent, setCustomContent] = useState('')
  const [templates, setTemplates] = useState<LibraryTemplate[]>([])

  const { data: addressData } = useAddresses()
  const senderAddresses = (addressData?.addresses ?? []).filter(
    (a: Address) => a.type === 'sender' || a.type === 'both'
  )

  // Load template library
  useEffect(() => {
    fetch('/api/v1/templates/library')
      .then(r => r.json() as Promise<{ templates: LibraryTemplate[] }>)
      .then((data) => setTemplates(data.templates))
      .catch(() => {})
  }, [])

  const handleDownloadSampleCSV = useCallback(() => {
    const csv = 'name,address_line1,suburb,state,postcode,message\nJane Smith,123 Main St,Sydney,NSW,2000,Happy birthday!\nJohn Doe,456 High St,Melbourne,VIC,3000,Thank you for everything!'
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'sendbot-bulk-sample.csv'
    a.click()
    URL.revokeObjectURL(url)
  }, [])

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      const rows = parseCSV(text)
      if (rows.length === 0) {
        setError('CSV file is empty or has no data rows.')
        return
      }

      const headers = Object.keys(rows[0]!)

      // Auto-map columns by name matching
      const autoMap: Record<string, string> = {}
      for (const required of REQUIRED_COLUMNS) {
        const match = headers.find(h =>
          h === required ||
          h.replace(/[\s_-]/g, '') === required.replace(/[\s_-]/g, '') ||
          h === required.replace(/_/g, ' ')
        )
        if (match) autoMap[required] = match
      }
      // Convert rows to recipients using auto-mapping
      const mapped = rows.map(row => {
        const r: Recipient = {
          name: row[autoMap['name'] ?? 'name'] ?? '',
          address_line1: row[autoMap['address_line1'] ?? 'address_line1'] ?? '',
          address_line2: row[autoMap['address_line2'] ?? 'address_line2'] ?? '',
          suburb: row[autoMap['suburb'] ?? 'suburb'] ?? '',
          state: row[autoMap['state'] ?? 'state'] ?? '',
          postcode: row[autoMap['postcode'] ?? 'postcode'] ?? '',
        }

        // Remaining columns go into merge_data
        const mergeData: Record<string, string> = {}
        const mappedValues = new Set(Object.values(autoMap))
        headers.forEach(h => {
          if (!mappedValues.has(h) && row[h]) {
            mergeData[h] = row[h]!
          }
        })
        if (Object.keys(mergeData).length > 0) {
          r.merge_data = mergeData
        }

        return r
      })

      setRecipients(mapped)
      setError(null)
      setStep('preview')
      toast.success(`${mapped.length} recipients loaded from CSV`)
    }
    reader.readAsText(file)
  }, [])

  const handlePreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/v1/letters/bulk/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipients,
          options: { colour, priority },
          from_address_id: senderAddressId || undefined,
          template_id: contentMode === 'template' ? selectedTemplate?.id : undefined,
          content: contentMode === 'custom' ? customContent : undefined,
        }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error || 'Preview failed')
      }
      const data = await res.json() as PreviewResult
      setPreview(data)
      setStep('confirm')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed')
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/v1/letters/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipients,
          options: { colour, priority },
          from_address_id: senderAddressId || undefined,
          template_id: contentMode === 'template' ? selectedTemplate?.id : undefined,
          content: contentMode === 'custom' ? customContent : undefined,
        }),
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error || 'Bulk send failed')
      }
      const data = await res.json() as BulkResult
      setResult(data)
      setStep('done')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk send failed')
    } finally {
      setLoading(false)
    }
  }

  if (step === 'done' && result) {
    return (
      <div className="container max-w-4xl py-8">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <h2 className="text-2xl font-bold">Bulk Send Complete</h2>
            <p className="text-muted-foreground">
              {result.created} letters created for {result.total_cost}
            </p>
            {result.errors.length > 0 && (
              <div className="w-full max-w-md">
                <p className="text-sm text-destructive mb-2">{result.errors.length} failed:</p>
                {result.errors.slice(0, 5).map((err, i) => (
                  <p key={i} className="text-xs text-muted-foreground">Row {err.row}: {err.error}</p>
                ))}
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <Button asChild variant="outline">
                <Link to="/dashboard/orders">View Orders</Link>
              </Button>
              <Button onClick={() => {
                setStep('upload')
                setRecipients([])
                setPreview(null)
                setResult(null)
                setSelectedTemplate(null)
                setCustomContent('')
              }}>
                Send Another Batch
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">Bulk Send</h1>
        <p className="text-muted-foreground">
          Upload a CSV of recipients to send multiple letters at once.
        </p>
      </div>

      <div className="space-y-6">
        {/* Step 1: Upload CSV */}
        <Card>
          <CardHeader>
            <CardTitle>1. Upload Recipients</CardTitle>
            <CardDescription>
              CSV must include columns: name, address_line1, suburb, state, postcode.
              Extra columns become merge data for templates.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-4">
              <Label htmlFor="csv-upload" className="cursor-pointer">
                <div className="flex items-center gap-2 rounded-lg border-2 border-dashed px-6 py-4 hover:border-primary transition-colors">
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {recipients.length > 0
                      ? `${recipients.length} recipients loaded`
                      : 'Choose CSV file...'}
                  </span>
                </div>
              </Label>
              <Input
                id="csv-upload"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
            <button
              type="button"
              onClick={handleDownloadSampleCSV}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Download className="h-3 w-3" />
              Download sample CSV
            </button>
          </CardContent>
        </Card>

        {/* Step 2: Preview recipients */}
        {step !== 'upload' && recipients.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>2. Review Recipients</CardTitle>
              <CardDescription>
                {recipients.length} recipients loaded from CSV.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Suburb</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Postcode</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipients.slice(0, 20).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-sm">{r.address_line1}</TableCell>
                        <TableCell>{r.suburb}</TableCell>
                        <TableCell>{r.state}</TableCell>
                        <TableCell>{r.postcode}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {recipients.length > 20 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Showing first 20 of {recipients.length} recipients.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Letter Content */}
        {step !== 'upload' && (
          <Card>
            <CardHeader>
              <CardTitle>3. Letter Content</CardTitle>
              <CardDescription>
                What should the letters say? Use a template or write custom content.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={contentMode} onValueChange={(v) => setContentMode(v as 'template' | 'custom')}>
                <TabsList className="mb-4">
                  <TabsTrigger value="template">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Use Template
                  </TabsTrigger>
                  <TabsTrigger value="custom">
                    <PenLine className="mr-2 h-4 w-4" />
                    Custom Content
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="template">
                  {templates.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">No templates available yet.</p>
                  ) : (
                    <div className="space-y-4">
                      <Select
                        value={selectedTemplate?.id ?? ''}
                        onValueChange={(id) => {
                          const t = templates.find(t => t.id === id)
                          if (t) setSelectedTemplate(t)
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a template..." />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name} {t.description ? `— ${t.description}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {selectedTemplate && (
                        <>
                          {selectedTemplate.merge_fields.length > 0 && (
                            <div className="rounded-lg border p-3">
                              <p className="text-xs text-muted-foreground mb-2">
                                Merge fields — these will be filled from CSV columns:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {selectedTemplate.merge_fields.map((f) => (
                                  <Badge key={f.name} variant="outline" className="text-xs">
                                    {`{{${f.name}}}`} → {f.label}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="rounded-lg bg-muted/50 p-3">
                            <p className="text-xs text-muted-foreground mb-1">Preview:</p>
                            <p className="text-sm">
                              {(() => {
                                let preview = selectedTemplate.content_preview
                                for (const field of selectedTemplate.merge_fields) {
                                  preview = preview.replace(
                                    new RegExp(`\\{\\{${field.name}\\}\\}`, 'g'),
                                    `[${field.label}]`
                                  )
                                }
                                return preview.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
                              })()}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="custom">
                  <Textarea
                    value={customContent}
                    onChange={(e) => setCustomContent(e.target.value)}
                    placeholder={"Dear {{name}},\n\nWrite your letter content here. Use {{field_name}} for merge fields from your CSV..."}
                    rows={10}
                    className="font-mono"
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Options */}
        {step !== 'upload' && (
          <Card>
            <CardHeader>
              <CardTitle>4. Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {senderAddresses.length > 0 && (
                <div>
                  <Label>Sender (return address)</Label>
                  <Select value={senderAddressId} onValueChange={setSenderAddressId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sender address..." />
                    </SelectTrigger>
                    <SelectContent>
                      {senderAddresses.map((addr: Address) => (
                        <SelectItem key={addr.id} value={addr.id}>
                          {addr.label} — {addr.name}, {addr.suburb} {addr.state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="bulk-colour">Colour printing</Label>
                  <p className="text-sm text-muted-foreground">+$1.00 per letter</p>
                </div>
                <Switch id="bulk-colour" checked={colour} onCheckedChange={setColour} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="bulk-priority">Priority post</Label>
                  <p className="text-sm text-muted-foreground">+$2.00 per letter</p>
                </div>
                <Switch id="bulk-priority" checked={priority} onCheckedChange={setPriority} />
              </div>

              {step === 'preview' && (
                <Button onClick={handlePreview} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Validate & Preview Cost
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 5: Confirm & send */}
        {step === 'confirm' && preview && (
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle>5. Confirm & Send</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-green-600">{preview.valid_count}</p>
                  <p className="text-sm text-muted-foreground">Valid</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{preview.error_count}</p>
                  <p className="text-sm text-muted-foreground">Errors</p>
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono">{preview.estimated_cost}</p>
                  <p className="text-sm text-muted-foreground">Total Cost</p>
                </div>
              </div>

              {preview.errors.length > 0 && (
                <div className="rounded-lg border border-destructive/20 p-3">
                  <p className="text-sm font-medium text-destructive flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {preview.error_count} rows have issues (will be skipped):
                  </p>
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                    {preview.errors.slice(0, 10).map((err, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        Row {err.row}: {err.errors.join(', ')}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleSend}
                disabled={loading || preview.valid_count === 0}
                size="lg"
                className="w-full"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send {preview.valid_count} Letters for {preview.estimated_cost}
              </Button>
            </CardContent>
          </Card>
        )}

        {error && (
          <p className="text-sm text-destructive">{error}</p>
        )}
      </div>
    </div>
  )
}
